import type { Game, Goals, Opponent, Player, Serve } from "../types";

export const demoGoals: Goals = {
  avgSpeedFloat: 72,
  avgSpeedHybrid: 78,
  avgSpeedSpin: 96,
  effiencyFloat: 58,
  effiencyHybrid: 64,
  effiencySpin: 70
};

export const demoPlayers: Player[] = [
  { id: "demo-1", firstName: "Maya", lastName: "Renaud", number: 4, position: "Setter", image: "", dateOfBirth: "2004-03-14", height: "5'10", weight: 148, yearsOfEligibility: 2 },
  { id: "demo-2", firstName: "Camille", lastName: "Nguyen", number: 9, position: "Outside Hitter", image: "", dateOfBirth: "2003-10-22", height: "6'1", weight: 164, yearsOfEligibility: 1 },
  { id: "demo-3", firstName: "Ari", lastName: "Moreau", number: 12, position: "Opposite", image: "", dateOfBirth: "2005-01-08", height: "6'2", weight: 171, yearsOfEligibility: 3 },
  { id: "demo-4", firstName: "Lea", lastName: "Tremblay", number: 16, position: "Middle Blocker", image: "", dateOfBirth: "2002-07-30", height: "6'3", weight: 176, yearsOfEligibility: 1 },
  { id: "demo-5", firstName: "Nora", lastName: "Diallo", number: 2, position: "Libero", image: "", dateOfBirth: "2004-11-05", height: "5'7", weight: 136, yearsOfEligibility: 2 },
  { id: "demo-6", firstName: "Elise", lastName: "Caron", number: 21, position: "Outside Hitter", image: "", dateOfBirth: "2005-05-19", height: "6'0", weight: 158, yearsOfEligibility: 4 }
];

const typeCycle = ["float", "hyb", "spin"] as const;
const baseDate = new Date("2026-09-08T12:00:00");

function stateFor(index: number, playerIndex: number): 1 | 2 | 3 {
  const score = (index * 7 + playerIndex * 5) % 20;
  if (score >= 16) return 3;
  if (score <= 3) return 1;
  return 2;
}

export const demoOpponents: Opponent[] = [
  { id: "demo-opp-1", name: "Riverside Hawks" },
  { id: "demo-opp-2", name: "Northgate VC" },
  { id: "demo-opp-3", name: "Lakeview Storm" }
];

function daysBefore(days: number) {
  const date = new Date(baseDate);
  date.setDate(baseDate.getDate() - days);
  return date;
}

function seasonFor(date: Date) {
  const year = date.getFullYear();
  const start = date.getMonth() >= 7 ? year : year - 1;
  return `${start}-${String(start + 1).slice(2)}`;
}

export const demoGames: Game[] = [2, 9, 16, 30, 44, 58, 72].map((days, index) => {
  const date = daysBefore(days);
  return {
    id: `demo-game-${index + 1}`,
    opponentId: demoOpponents[index % demoOpponents.length].id,
    playedOn: date.toISOString().slice(0, 10),
    season: seasonFor(date)
  };
});

// Opposing receivers per opponent, with how well each one passes (0–4 scale)
const demoReceivers: Record<string, Array<{ number: number; skill: number }>> = {
  "demo-opp-1": [{ number: 3, skill: 3.2 }, { number: 7, skill: 1.6 }, { number: 11, skill: 2.6 }, { number: 14, skill: 2.1 }],
  "demo-opp-2": [{ number: 1, skill: 2.9 }, { number: 5, skill: 2.2 }, { number: 8, skill: 1.4 }, { number: 17, skill: 3.4 }],
  "demo-opp-3": [{ number: 2, skill: 2.4 }, { number: 6, skill: 3.0 }, { number: 10, skill: 1.9 }, { number: 13, skill: 2.7 }]
};

// Serves recorded within a day of a game belong to it, with a graded reception
function withGameContext(serve: Serve, index: number): Serve {
  const game = demoGames.find((g) => serve.date && Math.abs(new Date(`${g.playedOn}T12:00:00`).getTime() - serve.date.getTime()) <= 36 * 3600 * 1000);
  if (!game) return serve;
  if (serve.state === 1) return { ...serve, gameId: game.id };
  const receivers = demoReceivers[game.opponentId];
  const receiver = receivers[index % receivers.length];
  const grade = serve.state === 3 ? 0 : Math.max(0, Math.min(4, Math.round(receiver.skill + (((index * 7) % 5) - 2) * 0.6)));
  return { ...serve, gameId: game.id, receiverNumber: receiver.number, receptionGrade: grade, receptionScale: [0, 4] };
}

export const demoServes: Serve[] = demoPlayers.flatMap((player, playerIndex) =>
  Array.from({ length: 34 + playerIndex * 3 }, (_, index) => {
    const type = typeCycle[(index + playerIndex) % typeCycle.length];
    const date = new Date(baseDate);
    date.setDate(baseDate.getDate() - ((index * 2 + playerIndex * 3) % 76));
    const typeBoost = type === "spin" ? 18 : type === "hyb" ? 9 : 0;
    const playerBoost = playerIndex * 1.8;
    const variation = ((index * 11) % 15) - 5;

    return {
      id: `${player.id}-serve-${index}`,
      playerId: player.id,
      state: stateFor(index, playerIndex),
      type,
      speed: Math.max(42, Math.round((62 + typeBoost + playerBoost + variation) * 10) / 10),
      date
    };
  })
).map(withGameContext);
